import { Box, Grid, styled } from "@mui/material";
import { observer } from "mobx-react";
import React, { FunctionComponent } from "react";
import { useStore } from "../../../../utils/useStore";
import AcxMainTextField from "../../../UI/AcxMainTextFieldGrid";
import AcxSelectMulti from "../../../UI/Select/BaseSelectComponents/AcxSelectMulti";
import {
    EvalReviewStore,
    LoadingAICoachingNotes,
} from "../Stores/EvalReviewStore";
import AcxButton from "components/UI/AcxButton";
import SparklingSvg from "SvgIcons/SparklingSvg";
import { AuthStore } from "stores/AuthStore";
import StopIcon from "@mui/icons-material/Stop";
import AgentLoading from "animations/AgentLoading";
import AgentStore from "components/KBo/Stores/AgentStore";
import { LoadingAICoachingNotesFromChat } from "components/KBo/Stores/SingleAgentChatStore";

interface IConvoEvalReviewDetailsFormProps {
    isCoached: boolean;
}

const GenerateNotesButton = styled(AcxButton)(({ theme, disabled }) => ({
    background:
        "linear-gradient(131deg, #32D1B7 3.43%, #349AC7 70.89%, #3664D6 100.5%)",
    paddingInline: theme.spacing(2),
    paddingBlock: theme.spacing(1),
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 600,
    opacity: disabled ? 0.5 : 1,
    filter: disabled ? "grayscale(100%)" : "none",
}));

const ConvoEvalReviewDetailsForm: FunctionComponent<IConvoEvalReviewDetailsFormProps> =
    observer((props) => {
        const store = useStore(EvalReviewStore);
        const authStore = useStore(AuthStore);
        const agentStore = useStore(AgentStore);

        function onImproveNotesChange(
            evt: React.ChangeEvent<HTMLInputElement>,
        ) {
            const val = evt.target.value;
            store.agentCoaching?.setImprovementNotes(val);
        }

        function onDoWellChange(evt: React.ChangeEvent<HTMLInputElement>) {
            const val = evt.target.value;
            store.agentCoaching?.setDoWellNotes(val);
        }

        const loadingAICoachingNotes =
            store.getTaskLoadingV2(LoadingAICoachingNotes) ||
            agentStore.currentChatStore.getTaskLoadingV2(
                LoadingAICoachingNotesFromChat,
            );

        return (
            <Grid container item direction="column" flexWrap="nowrap">
                <Grid item padding={2}>
                    <AcxMainTextField
                        id={"eval-coaching-do-well-notes"}
                        multiLine
                        rows={4}
                        rowsMax={6}
                        labelText={"What did the agent do well?"}
                        placeholderText={
                            loadingAICoachingNotes ? "" : "Add feedback..."
                        }
                        disabled={props.isCoached || loadingAICoachingNotes}
                        onChange={onDoWellChange}
                        value={store.agentCoaching?.agentDoWellNotes ?? ""}
                        sx={(theme) => ({
                            padding: theme.spacing(2),
                        })}
                        labelSx={(theme) => ({
                            color: theme.palette.gray[600],
                            fontSize: "14px",
                            fontWeight: 600,
                            lineHeight: "20px",
                        })}
                        textContainerSx={(theme) => ({
                            borderColor: theme.palette.gray[200],
                            ":focus-within": {
                                borderColor: theme.palette.primary.main,
                            },
                        })}
                        startAdornment={
                            loadingAICoachingNotes ? (
                                <Grid container height={"80px"} width={"40px"}>
                                    <Box height={"30px"} ml={"-10px"}>
                                        <AgentLoading />
                                    </Box>
                                </Grid>
                            ) : undefined
                        }
                    />
                </Grid>

                <Grid item padding={2} pt={0}>
                    <AcxMainTextField
                        id={"eval-coaching-improvement-notes"}
                        multiLine
                        rows={4}
                        rowsMax={6}
                        labelText={"What areas of opportunity exist?"}
                        placeholderText={
                            loadingAICoachingNotes ? "" : "Add feedback..."
                        }
                        disabled={props.isCoached || loadingAICoachingNotes}
                        onChange={onImproveNotesChange}
                        value={store.agentCoaching?.improvementNotes ?? ""}
                        sx={(theme) => ({
                            padding: theme.spacing(2),
                        })}
                        labelSx={(theme) => ({
                            color: theme.palette.gray[600],
                            fontSize: "14px",
                            fontWeight: 600,
                            lineHeight: "20px",
                        })}
                        textContainerSx={(theme) => ({
                            borderColor: theme.palette.gray[200],
                            ":focus-within": {
                                borderColor: theme.palette.primary.main,
                            },
                        })}
                        startAdornment={
                            loadingAICoachingNotes ? (
                                <Grid container height={"80px"} width={"40px"}>
                                    <Box height={"30px"} ml={"-10px"}>
                                        <AgentLoading />
                                    </Box>
                                </Grid>
                            ) : undefined
                        }
                    />
                </Grid>

                <Grid item padding={2} pt={0}>
                    <AcxSelectMulti
                        options={store.focusAreaQuestions ?? []}
                        id={"eval-coaching-focus-areas-selector"}
                        containerHeight={"auto"}
                        valueField={"value"}
                        fullWidth
                        isDisabled={props.isCoached}
                        onChange={store.agentCoaching?.setFocusAreas}
                        defaultValue={store.agentCoaching?.focusAreaQuestions}
                        labelField={"value"}
                        inputLabel={"Focus Areas"}
                    />
                </Grid>

                {authStore.canUserEdit("AI Generated Coaching Notes") &&
                    !props.isCoached && (
                        <Grid
                            container
                            item
                            padding={2}
                            justifyContent="flex-end"
                        >
                            <Grid item>
                                {loadingAICoachingNotes ? (
                                    <GenerateNotesButton
                                        fullWidth={false}
                                        startIcon={<StopIcon />}
                                        onClick={() => {
                                            store.cancelCoachingNotes();
                                            agentStore.currentChatStore.abort();
                                        }}
                                    >
                                        Stop
                                    </GenerateNotesButton>
                                ) : (
                                    <GenerateNotesButton
                                        fullWidth={false}
                                        startIcon={
                                            <SparklingSvg
                                                width={20}
                                                height={20}
                                                color="#FFFFFF"
                                                gradient={false}
                                            />
                                        }
                                        disabled={
                                            store.hasGeneratedCoachingNotes ||
                                            !store.coachingAudioMetadataId ||
                                            store.getTaskLoading(
                                                "Load Single Evaluation Review",
                                            )
                                        }
                                        loading={store.getTaskLoading(
                                            "Generate Coaching Notes",
                                        )}
                                        onClick={() =>
                                            store.generateCoachingNotes()
                                        }
                                        id="generate-AI-coaching-notes"
                                    >
                                        Generate Notes
                                    </GenerateNotesButton>
                                )}
                            </Grid>
                        </Grid>
                    )}
            </Grid>
        );
    });

export default ConvoEvalReviewDetailsForm;
